import styled, { ThemedStyledProps } from 'styled-components';
import { Chip as BaseChip, ChipProps } from '@mui/material';
import { Theme } from '@mui/material/styles/createTheme';
import { ReactComponent as CloseIcon } from '../../assets/close-2.svg';
import SvgIcon from '../SvgIcon';

export type ChipColor = ChipProps['color'] | 'idle' | 'merged' | 'primary';

export type Props = Omit<ChipProps, 'color'> & {
  size?: 'small' | 'medium';
  bold?: boolean;
  customVariant?: 'dashed' | 'crossed' | 'rounded';
  overflow?: 'visible' | 'hidden';
};

type InnerProps = Props & { $color?: ChipColor };

const StyledChip = styled(BaseChip).attrs((props: InnerProps) => ({
  'aria-label': props['aria-label'] || 'chip',
}))<InnerProps>`
  border-radius: ${({ variant, customVariant }) =>
    variant === 'outlined' || customVariant === 'rounded' ? '16' : 4}px;
  font-size: 14px;
  font-weight: ${({ bold, theme }) =>
    bold ? theme.typography.fontWeightBold : 400};
  padding: ${({ onDelete, variant, customVariant }) =>
    variant === 'outlined' || customVariant === 'rounded'
      ? onDelete
        ? '4px 6px 6px 6px'
        : '4px 10px 6px 6px'
      : onDelete
      ? '4px 4px 6px 6px'
      : '4px 6px'};
  height: fit-content;
  gap: 4px;
  overflow: hidden;

  & .MuiChip-label {
    padding-top: 0px;
    padding-bottom: 0;
    padding-left: ${({ variant, customVariant }) =>
      variant === 'outlined' || customVariant === 'rounded' ? 3 : 0}px;
    overflow: ${({ overflow }) => overflow || 'hidden'};
    padding-right: ${({ onDelete }) => (!onDelete ? '0' : '12px')};
  }

  .MuiChip-icon {
    margin: 0;
  }

  .MuiChip-deleteIcon {
    position: relative;

    &::after {
      content: '';
      position: absolute;
      top: -10px;
      bottom: -10px;
      left: -6px;
      right: -15px;
      cursor: pointer;
      border-radius: 0 4px 4px 0;
      transition: background-color 0.2s ease;
    }

    &:hover::after {
      background-color: ${({ theme }) => theme.palette.action.hover};
    }
  }

  ${({ customVariant, theme }) =>
    customVariant === 'dashed'
      ? `
    border: 1px dashed ${theme.palette.common.gray18};
    `
      : customVariant === 'crossed'
      ? `
        border: 1px dashed ${theme.palette.common.gray18};
        text-decoration: line-through;
    `
      : ``};

  ${({
    $color,
    theme,
    variant,
    customVariant,
  }: ThemedStyledProps<InnerProps, Theme>) =>
    $color === 'error'
      ? `
    color: ${theme.palette.common.red};
    background: ${theme.palette.common.lightRed};
    `
      : $color === 'warning'
      ? `
    color: ${theme.palette.common.orange};
    background: ${theme.palette.common.lightOrange};
    `
      : $color === 'success'
      ? `
    color: ${theme.palette.common.green};
    background: ${theme.palette.common.lightTurqoise};
    `
      : $color === 'idle'
      ? `
    color: ${theme.palette.common.blue7};
    background: ${theme.palette.common.lightBlue4};
    `
      : $color === 'merged'
      ? `
    color: ${theme.palette.common.purple};
    background: ${theme.palette.common.lightPurple};
    `
      : $color === 'primary'
      ? `
    color: ${theme.palette.secondary.main};
    background: ${theme.palette.common.lightBlue4};
    `
      : $color === 'default' || !$color
      ? `
    color: ${theme.palette.common.gray2};
    background: ${
      variant === 'outlined' || customVariant === 'rounded'
        ? theme.palette.common.gray6
        : customVariant
        ? theme.palette.common.white
        : theme.palette.common.gray6
    };
    `
      : ``};

  ${({ size }) =>
    size === 'small'
      ? `
      font-size: 11px;
      min-width: 22px;
      height: 18px;
    `
      : size === 'medium'
      ? `
      font-size: 11px;
      height: 24px;
      `
      : `height: 28px;`};
`;

const Chip = ({ color, ...props }: Props & { color?: ChipColor }) => (
  <StyledChip
    $color={color}
    deleteIcon={
      <SvgIcon width={10}>
        <CloseIcon />
      </SvgIcon>
    }
    {...props}
  />
);

export default Chip;
